import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from 'src/auth/FirebaseAuthContext';
import { useHomePath } from 'src/auth/useHomePath';

import {
  captureFunnelEvent,
  type ProjectPageActionType,
} from '../../services/telemetry';

/**
 * Shown when `GET /project/{id}` answers 404: the project was deleted or
 * isn't on the signed-in account (old or shared link, other account).
 */
export function ProjectNotFound({
  projectId,
  type,
}: {
  projectId: string;
  type: ProjectPageActionType;
}) {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { user } = useFirebaseAuth();
  const isGuest = !!user?.isAnonymous;

  useEffect(() => {
    captureFunnelEvent('project_not_found', {
      project_id: projectId,
      type,
      is_guest: isGuest,
    });
  }, [projectId, type, isGuest]);

  return (
    <div
      className={`
        my-4 flex flex-col items-center px-4 font-[Outfit]
        sm:my-6 sm:px-6
      `}
    >
      <div
        className={`
          w-full max-w-3xl rounded-lg bg-white p-4 text-center text-gray-600
          sm:p-6
          md:p-8
        `}
      >
        <h2
          className={`mb-3 text-xl font-bold text-gray-800 sm:mb-4 sm:text-2xl`}
        >
          Project not found
        </h2>
        <p className="mb-4 text-sm text-gray-700 sm:text-base">
          {isGuest ? (
            <>
              This project was either deleted or isn&apos;t available in this
              guest session. If you created it with an account, sign in to open
              it.
            </>
          ) : (
            <>
              This project was either deleted or isn&apos;t on the account
              you&apos;re signed in with
              {user?.email ? (
                <>
                  {' '}
                  (<span className="font-medium">{user.email}</span>)
                </>
              ) : null}
              . If you created it with a different account, sign in with that
              account to open it.
            </>
          )}
        </p>
        <button
          // Replace, so Back doesn't return to this dead link.
          onClick={() => navigate(homePath, { replace: true })}
          className={`
            focus:ring-opacity-50 focus:ring-2 focus:ring-yellow-500
            focus:outline-none
            min-h-[44px] min-w-[44px] cursor-pointer rounded bg-yellow-500 px-4
            py-2 text-sm text-white transition-transform duration-100
            hover:bg-yellow-600
            active:scale-95 active:bg-yellow-700
            sm:text-base
          `}
        >
          {homePath === '/workspace' ? 'Go to My Projects' : 'Go to Home Page'}
        </button>
      </div>
    </div>
  );
}
